/**
 * Same-origin path validator for the unified magic-link `?next=` parameter
 * (issue #1477).
 *
 * After a signer clicks a magic link, `LoginByToken.tsx` reads `?next=` and
 * navigates to it. Because the magic link is delivered by email, an attacker
 * who can convince a user to click their crafted URL could otherwise pivot
 * the freshly authenticated signer to an attacker-controlled origin. This
 * validator is the SOLE defense against that open-redirect class of attack.
 *
 * The contract is intentionally strict: only same-origin absolute paths.
 * If you ever loosen it (e.g., to allow a redirect to a sister subdomain),
 * audit every existing call site of {@link resolveNextOrDefault} first.
 *
 * Pinned attack vectors are covered by `safe-next.test.ts` — do not delete
 * those tests.
 *
 * @param value the candidate `?next=` value (already URL-decoded)
 * @returns `true` only if `value` is a string that is safe to pass to
 *   `react-router`'s `navigate()`
 */
export function isSafeNextPath(value: unknown): value is string {
    if (typeof value !== 'string') return false;
    if (value.length === 0) return false;
    // Reject any leading or trailing whitespace; some browsers strip these
    // before parsing, which can smuggle protocol-relative URLs past naive
    // `startsWith('//')` checks.
    if (value !== value.trim()) return false;
    if (!value.startsWith('/')) return false;
    // Protocol-relative: `//evil.com` is parsed by every browser as
    // `https://evil.com`.
    if (value.startsWith('//')) return false;
    // Backslash normalization: some routers / browsers treat `/\` as `//`.
    if (value.startsWith('/\\')) return false;
    // Defense in depth: `://` should never appear in a same-origin absolute
    // path. Rejecting it blocks both scheme-prefixed inputs that somehow
    // pass the earlier checks and pathological paths like `/foo://bar`.
    if (value.includes('://')) return false;
    return true;
}

/**
 * Returns `value` if it is a safe same-origin path; otherwise returns
 * `fallback`. The fallback is always returned for `null`, `undefined`,
 * non-string values, and any input that fails {@link isSafeNextPath}.
 *
 * @param value the candidate `?next=` value (already URL-decoded)
 * @param fallback the safe default to use when `value` is unsafe
 * @returns either `value` (validated) or `fallback`
 */
export function resolveNextOrDefault(value: unknown, fallback: string): string {
    return isSafeNextPath(value) ? value : fallback;
}
